import React, { useEffect, useState } from 'react';
import { Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Check, ArrowRight } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { useCart } from '@/context/CartContext';
import { PAYMENT_URL } from '@/data/products';
import { usePageMeta } from '@/hooks/usePageMeta';

const AUTO_REDIRECT_SECONDS = 8;

export const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const order = location.state?.order;

  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS);
  const [redirected, setRedirected] = useState(false);

  // Product-specific payment URL takes priority over the default studio URL
  const paymentUrl = order?.paymentUrl || PAYMENT_URL;

  usePageMeta({
    title: 'Order Received — Graves Collective',
    description: 'Thank you for your order. Continuing to secure payment.',
  });

  // Clear cart when confirmation page loads with a valid order
  useEffect(() => {
    if (order) clearCart();
    // We intentionally only run this once on mount when the order arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect countdown
  useEffect(() => {
    if (!order || redirected) return;
    if (secondsLeft <= 0) {
      setRedirected(true);
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, order, redirected]);

  if (!order) {
    return <Navigate to="/" replace />;
  }

  const handleManualRedirect = () => {
    setRedirected(true);
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="page-order-confirmation">
      <SiteHeader />

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-16 lg:pt-24 pb-24">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-terracotta/10 flex items-center justify-center">
            <Check className="w-8 h-8 text-terracotta" strokeWidth={1.5} />
          </div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-4">
            Order #{(order.id || '').slice(0, 8).toUpperCase()}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Thank you, <span className="italic text-terracotta">{(order.shippingInfo?.fullName || '').split(' ')[0] || 'friend'}.</span>
          </h1>
          <p className="mt-8 text-lg text-soft leading-relaxed max-w-xl mx-auto">
            We&apos;ve received your order. A confirmation has been sent to{' '}
            <span className="text-ink">{order.shippingInfo?.email}</span>. The final step is payment —
            we use Square to keep things secure.
          </p>
        </div>

        {/* Order Summary */}
        <div className="border border-ink/10 bg-cream-deep/40 p-8 sm:p-10 mb-12">
          <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">Order Summary</p>

          <div className="space-y-4 mb-6">
            {(order.items || []).map((item, idx) => (
              <div
                key={`${item.id}-${item.size || ''}-${idx}`}
                className="flex gap-4 pb-4 border-b border-ink/10 last:border-0"
              >
                {item.image && (
                  <div className="w-16 h-16 bg-ink/[0.04] flex-shrink-0 overflow-hidden">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-base text-ink truncate">{item.title}</p>
                  <p className="text-xs text-soft mt-1">
                    {item.size ? `Size ${item.size} · ` : ''}Qty {item.quantity}
                  </p>
                </div>
                <p className="text-sm text-ink tabular-nums">
                  {item.price ? `$${(item.price * item.quantity).toFixed(2)}` : 'Inquire'}
                </p>
              </div>
            ))}
          </div>

          <div className="space-y-2 pt-4 border-t border-ink/10 text-sm">
            <div className="flex justify-between"><span className="text-soft">Subtotal</span><span className="tabular-nums">${(order.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-soft">Shipping ({order.deliveryMethod === 'pickup' ? 'Local Pickup' : 'Ship to me'})</span><span className="tabular-nums">{(order.shippingCost || 0) === 0 ? 'Free' : `$${order.shippingCost.toFixed(2)}`}</span></div>
            <div className="flex justify-between"><span className="text-soft">Tax</span><span className="tabular-nums">${(order.taxAmount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between font-display text-xl pt-3 mt-3 border-t border-ink/10">
              <span>Total</span>
              <span className="tabular-nums">${(order.orderTotal || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Pickup details (only for local pickup orders) */}
        {order.deliveryMethod === 'pickup' && (
          <div
            className="border border-terracotta/30 bg-terracotta/5 p-8 sm:p-10 mb-12"
            data-testid="pickup-details"
          >
            <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-3">
              Local Pickup Details
            </p>
            <h3 className="font-display text-2xl text-ink mb-4 tracking-tight">
              Pick up at the studio
            </h3>
            <address className="text-base text-ink leading-relaxed not-italic mb-4">
              900 Santa Fe Drive, Studio D
              <br />
              Denver, CO 80204
            </address>
            <p className="text-sm text-soft leading-relaxed mb-4">
              We&apos;ll email you when your order is ready. Best times to swing
              by: during{' '}
              <a
                href="https://denversantafe.org/first-friday-art-walk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink hover:text-terracotta transition-colors underline decoration-ink/20 hover:decoration-terracotta"
              >
                First Friday Art Walks
              </a>{' '}
              or by appointment.
            </p>
            <a
              href="https://maps.google.com/?q=900+Santa+Fe+Drive+Studio+D+Denver+CO+80204"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
              data-testid="pickup-directions-link"
            >
              Get directions
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        )}

        {/* Payment CTA */}
        <div className="text-center">
          <button
            onClick={handleManualRedirect}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-5 bg-ink text-cream hover:bg-terracotta transition-colors text-xs uppercase tracking-[0.18em] font-medium"
            data-testid="continue-to-payment-btn"
          >
            Continue to Payment
            <ArrowUpRight className="w-4 h-4" />
          </button>

          {!redirected && (
            <p className="mt-6 text-sm text-soft" data-testid="auto-redirect-notice">
              Redirecting automatically in <span className="text-ink tabular-nums">{secondsLeft}s</span>...
            </p>
          )}

          {redirected && (
            <p className="mt-6 text-sm text-soft">
              Payment window opened in a new tab. Didn&apos;t see it?{' '}
              <button onClick={handleManualRedirect} className="underline text-ink hover:text-terracotta" data-testid="reopen-payment">
                Reopen payment
              </button>
            </p>
          )}

          <div className="mt-12 pt-10 border-t border-ink/10 text-sm text-soft">
            <p className="mb-6">
              Questions about your order? Email{' '}
              <a href="mailto:joseph@josephgravesjr.com" className="text-ink hover:text-terracotta underline">
                joseph@josephgravesjr.com
              </a>
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] hover:text-terracotta transition-colors group"
              data-testid="return-home-btn"
            >
              Return to the studio
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default OrderConfirmationPage;
